import { Deferred, Effect, Exit, Ref, Scope } from "effect";
export interface ChatQuery {
  readonly identity: object;
  readonly scope: Scope.Closeable;
  readonly close: Effect.Effect<void>;
}
/** A closing child can already be detached from Scope.fork's parent link.
 * Keep its joined receipt until finalization actually settles. */
export const makeChatQueryScopes = Effect.fn("Frontend.Chat.makeQueryScopes")(function* (
  owner: Scope.Scope, retain: (exit: Exit.Exit<void>) => Effect.Effect<void>
) {
  const life = yield* Ref.make<{ readonly closed: boolean; readonly queries: ReadonlyMap<object, ChatQuery> }>({ closed: false, queries: new Map() });
  yield* Effect.addFinalizer(() => Effect.uninterruptible(Effect.gen(function* () {
    const pending = yield* Ref.getAndUpdate(life, current => ({ ...current, closed: true }));
    yield* Effect.forEach(pending.queries.values(), query => Effect.exit(query.close), { discard: true });
  })));
  const open = Effect.uninterruptible(Effect.gen(function* () {
    if (owner.state._tag === "Closed" || (yield* Ref.get(life)).closed) return yield* Effect.interrupt;
    const scope = yield* Scope.fork(owner), identity = {};
    const closing = yield* Ref.make(false), receipt = yield* Deferred.make<Exit.Exit<void>>();
    const close = Effect.uninterruptible(Effect.gen(function* () {
      if (yield* Ref.getAndSet(closing, true)) return yield* (yield* Deferred.await(receipt));
      const result = yield* Effect.exit(Scope.close(scope, Exit.void));
      yield* retain(result);
      yield* Ref.update(life, current => { const queries = new Map(current.queries); queries.delete(identity); return { ...current, queries }; });
      yield* Deferred.succeed(receipt, result); return yield* result;
    }));
    const query: ChatQuery = { identity, scope, close };
    const admitted = yield* Ref.modify(life, current => current.closed || owner.state._tag === "Closed"
      ? [false, current] as const : [true, { ...current, queries: new Map(current.queries).set(identity, query) }] as const);
    if (!admitted) { yield* close; return yield* Effect.interrupt; }
    return query;
  }));
  return { open };
});
